import React, { useEffect, useMemo } from 'react';
import {
  AppBar,
  Box,
  Breadcrumbs,
  Link,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { NavigateNext } from '@mui/icons-material';
import { AppIconButton } from './design-system';
import SiteFloorPopoverButton from './location/SiteFloorPopoverButton';
import { SubscriptionToolbarNotice } from './common/SubscriptionToolbarNotice';
import {
  APP_TOOLBAR_SECONDARY_HEIGHT,
  useAppToolbarContext,
  useAppToolbarPageState,
} from '../contexts/AppToolbarContext';
import type { AppToolbarBreadcrumb } from '../utils/navigationBreadcrumbs';
import {
  isDashboardPath,
  isLocationLayoutPath,
  isProviderLocationToolbarPath,
} from '../utils/appToolbarRoutes';
import { getAppToolbarChrome } from '../theme/uiSemanticColors';

export interface AppShellToolbarProps {
  cleanPath: string;
  breadcrumbs: AppToolbarBreadcrumb[];
  drawerOpen: boolean;
  drawerWidth: number;
  collapsedDrawerWidth: number;
  onDrawerToggle: () => void;
  drawerToggleIcon: React.ReactNode;
  themeToggle: React.ReactNode;
  globalActions: React.ReactNode;
  platformName: string;
  zIndex: number;
}

const AppShellToolbar: React.FC<AppShellToolbarProps> = ({
  cleanPath,
  breadcrumbs,
  drawerOpen,
  drawerWidth,
  collapsedDrawerWidth,
  onDrawerToggle,
  drawerToggleIcon,
  themeToggle,
  globalActions,
  platformName,
  zIndex,
}) => {
  const theme = useTheme();
  const toolbarChrome = useMemo(() => getAppToolbarChrome(theme), [theme]);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { setSecondaryToolbarActive } = useAppToolbarContext();
  const pageState = useAppToolbarPageState();
  const showLocationSelector = isLocationLayoutPath(cleanPath);
  const showProviderSecondary = isProviderLocationToolbarPath(cleanPath);
  const showPlatformName = isDashboardPath(cleanPath);
  const hasProviderSecondary = showProviderSecondary;
  const hasLocationMobileSecondary = showLocationSelector && isMobile;
  const hasPageContextSecondary = Boolean(pageState.contextControls);
  const hasSecondaryRow =
    hasProviderSecondary || hasLocationMobileSecondary || hasPageContextSecondary;

  useEffect(() => {
    setSecondaryToolbarActive(hasSecondaryRow);
    return () => setSecondaryToolbarActive(false);
  }, [hasSecondaryRow, setSecondaryToolbarActive]);

  const appBarWidth = useMemo(
    () => `calc(100% - ${drawerOpen ? drawerWidth : collapsedDrawerWidth}px)`,
    [collapsedDrawerWidth, drawerOpen, drawerWidth],
  );

  const appBarMargin = useMemo(
    () => `${drawerOpen ? drawerWidth : collapsedDrawerWidth}px`,
    [collapsedDrawerWidth, drawerOpen, drawerWidth],
  );

  const transition = theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  });

  const pageActionsSx = useMemo(
    () => ({
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      '& .MuiIconButton-root': {
        color: toolbarChrome.iconColor,
      },
      '& .MuiIconButton-root:hover:not(.Mui-disabled)': {
        color: toolbarChrome.iconHoverColor,
        backgroundColor: toolbarChrome.iconHoverBg,
      },
    }),
    [toolbarChrome],
  );

  return (
    <AppBar
      position="fixed"
      color="default"
      elevation={0}
      sx={{
        zIndex,
        width: appBarWidth,
        ml: appBarMargin,
        transition,
        bgcolor: toolbarChrome.barBg,
        color: 'text.primary',
        borderBottom: 1,
        borderColor: toolbarChrome.barBorder,
      }}
    >
      <Toolbar sx={{ gap: 1, minHeight: { xs: 56, sm: 64 }, position: 'relative' }}>
        <AppIconButton
          aria-label="toggle drawer"
          onClick={onDrawerToggle}
          edge="start"
          sx={{
            mr: 0.5,
            flexShrink: 0,
            color: toolbarChrome.iconColor,
            '&:hover': {
              color: toolbarChrome.iconHoverColor,
              backgroundColor: toolbarChrome.iconHoverBg,
            },
          }}
        >
          {drawerToggleIcon}
        </AppIconButton>

        {showPlatformName ? (
          <Typography variant="h6" noWrap sx={{ flexShrink: 0, mr: 2 }}>
            {platformName}
          </Typography>
        ) : null}

        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <Breadcrumbs
            separator={<NavigateNext fontSize="small" />}
            aria-label="breadcrumb"
            sx={{
              color: 'text.secondary',
              '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
              '& .MuiBreadcrumbs-li': { minWidth: 0 },
              '& .MuiBreadcrumbs-separator': { color: 'text.disabled' },
            }}
          >
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1;
              if (crumb.href && !isLast) {
                return (
                  <Link
                    key={`${crumb.label}-${index}`}
                    color="text.secondary"
                    href={crumb.href}
                    underline="hover"
                    sx={{
                      fontSize: '0.875rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'block',
                      maxWidth: { xs: 120, sm: 200, md: 280 },
                      '&:hover': { color: 'text.primary' },
                    }}
                  >
                    {crumb.label}
                  </Link>
                );
              }
              return (
                <Typography
                  key={`${crumb.label}-${index}`}
                  color={isLast ? 'text.primary' : 'text.secondary'}
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: isLast ? 600 : 400,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: { xs: 160, sm: 280, md: 420 },
                  }}
                >
                  {crumb.label}
                </Typography>
              );
            })}
          </Breadcrumbs>
        </Box>

        {showLocationSelector ? (
          <Box sx={{ flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
            <SiteFloorPopoverButton />
          </Box>
        ) : null}

        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
            maxWidth: { md: 'min(520px, calc(100% - 360px))', lg: 'min(640px, calc(100% - 400px))' },
            pointerEvents: 'none',
            zIndex: 0,
          }}
        >
          <Box sx={{ pointerEvents: 'auto', minWidth: 0, maxWidth: '100%' }}>
            <SubscriptionToolbarNotice />
          </Box>
        </Box>

        {pageState.pageActions ? (
          <Box sx={pageActionsSx}>{pageState.pageActions}</Box>
        ) : null}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            gap: 0.5,
            '& .MuiIconButton-root': {
              color: toolbarChrome.iconColor,
            },
            '& .MuiIconButton-root:hover:not(.Mui-disabled)': {
              color: toolbarChrome.iconHoverColor,
              backgroundColor: toolbarChrome.iconHoverBg,
            },
          }}
        >
          {themeToggle}
          {globalActions}
        </Box>
      </Toolbar>

      {hasSecondaryRow ? (
        <Toolbar
          variant="dense"
          sx={{
            minHeight: APP_TOOLBAR_SECONDARY_HEIGHT,
            gap: 2,
            px: 2,
            borderTop: 1,
            borderColor: toolbarChrome.secondaryBorder,
            bgcolor: toolbarChrome.secondaryBg,
            color: 'text.primary',
            flexWrap: 'wrap',
          }}
        >
          {hasProviderSecondary || hasPageContextSecondary ? pageState.contextControls : null}
          {hasLocationMobileSecondary ? <SiteFloorPopoverButton /> : null}
        </Toolbar>
      ) : null}
    </AppBar>
  );
};

export default AppShellToolbar;
